/**
 * Raw SVG Object Component for rendering inline SVG strings, diagrams, and vector illustrations.
 * Supports:
 * - Parsing raw SVG XML strings and vector shapes.
 * - Time-synchronized sub-element highlighting (`highlightPart`).
 * - Animated speech sync: highlights specific diagram parts & labels when spoken by character.
 * - Embedded high-resolution image SVG rendering.
 */
import { SVGObject, SVGObjectConfig } from './SVGObject';
import { Renderer } from '../core/Renderer';

export interface RawSVGProps {
  svgString?: string;
  imageUrl?: string;
  src?: string;
  width?: number;
  height?: number;
  fill?: string;
  stroke?: string;
  strokeWidth?: number;
  activePart?: string;
  [key: string]: any;
}

export class RawSVGObject extends SVGObject {
  public props: RawSVGProps;
  
  private activePartId: string | null = null;
  private highlightTimer: number = 0;
  private highlightDuration: number = 0;
  private highlightColor: string = '#fde047';

  constructor(config: SVGObjectConfig & { props?: RawSVGProps }) {
    super({ ...config, type: 'svg' });
    this.props = { ...config.props };
    if (this.props.activePart) {
      this.activePartId = this.props.activePart;
    }
  }

  public render(): void {
    this.container.innerHTML = '';

    if (this.props.svgString) {
      try {
        let svgContent = this.props.svgString.trim();
        // Remove outer <svg...> wrapper if present so inner elements mount into <g>
        if (svgContent.toLowerCase().startsWith('<svg')) {
          svgContent = svgContent.replace(/<svg[^>]*>/i, '').replace(/<\/svg>/i, '');
        }

        const parser = new DOMParser();
        const doc = parser.parseFromString(
          `<svg xmlns="http://www.w3.org/2000/svg">${svgContent}</svg>`,
          'image/svg+xml'
        );

        // Check for parser error
        const parserError = doc.querySelector('parsererror');
        if (parserError) {
          console.warn('[RawSVGObject] SVG parse error, using innerHTML fallback:', parserError.textContent);
          this.container.innerHTML = svgContent;
          return;
        }

        // Import nodes properly using document.importNode to avoid DOMException
        const elements = Array.from(doc.documentElement.childNodes);
        elements.forEach(node => {
          if (node.nodeType === 1 || node.nodeType === 3) {
            const importedNode = document.importNode(node, true);
            this.container.appendChild(importedNode);
          }
        });

        // Apply active part highlight if set
        if (this.activePartId) {
          this.applyPartHighlight(this.activePartId, this.highlightColor, 1.0);
        }
      } catch (err) {
        console.error('[RawSVGObject] Error mounting SVG nodes:', err);
        try {
          this.container.innerHTML = this.props.svgString;
        } catch (e) {
          console.error('[RawSVGObject] innerHTML fallback failed:', e);
        }
      }
    } else if (this.props.imageUrl || this.props.src) {
      // Render image directly inside SVG container
      const width = this.props.width || 440;
      const height = this.props.height || 560;

      // Card Background
      const rect = document.createElementNS('http://www.w3.org/2000/svg', 'rect');
      rect.setAttribute('x', String(-width / 2));
      rect.setAttribute('y', String(-height / 2));
      rect.setAttribute('width', String(width));
      rect.setAttribute('height', String(height));
      rect.setAttribute('rx', '12');
      rect.setAttribute('fill', '#0f172a');
      rect.setAttribute('stroke', '#38bdf8');
      rect.setAttribute('stroke-width', '4');
      this.container.appendChild(rect);

      // Embedded Image Element
      const imgEl = document.createElementNS('http://www.w3.org/2000/svg', 'image');
      imgEl.setAttribute('href', this.props.imageUrl || this.props.src || '');
      imgEl.setAttribute('x', String(-width / 2 + 10));
      imgEl.setAttribute('y', String(-height / 2 + 10));
      imgEl.setAttribute('width', String(width - 20));
      imgEl.setAttribute('height', String(height - 20));
      imgEl.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      this.container.appendChild(imgEl);
    }
  }

  /**
   * Trigger time-synchronized highlight on a specific diagram part & label.
   */
  public highlightPart(partId: string, duration: number = 3.0, color: string = '#fde047'): void {
    this.activePartId = partId.toLowerCase();
    this.highlightDuration = duration;
    this.highlightTimer = duration;
    this.highlightColor = color;
    this.applyPartHighlight(this.activePartId, color, 1.0);
  }

  public clearHighlight(): void {
    this.activePartId = null;
    this.highlightTimer = 0;
    this.resetAllParts();
  }

  public override update(time: number, delta: number): void {
    super.update(time, delta);

    if (this.activePartId && this.highlightTimer > 0) {
      this.highlightTimer -= delta;
      const progress = Math.max(0, this.highlightTimer / this.highlightDuration);
      
      // Calculate pulsing glow effect
      const pulse = 1.0 + Math.sin(time * 8.0) * 0.12;
      this.applyPartHighlight(this.activePartId, this.highlightColor, pulse);

      if (this.highlightTimer <= 0) {
        this.clearHighlight();
      }
    }
  }

  private applyPartHighlight(partId: string, color: string, pulseScale: number): void {
    if (!this.container) return;

    const normalizedPart = partId.toLowerCase().trim();
    const allParts = this.container.querySelectorAll('[id^="part-"], [id^="label-"], [class*="part-"], [class*="label-"]');

    allParts.forEach(el => {
      const element = el as SVGElement;
      const elId = (element.id || element.className.baseVal || '').toLowerCase();

      // Preserve original transform if present
      if (!element.hasAttribute('data-orig-transform')) {
        element.setAttribute('data-orig-transform', element.getAttribute('transform') || '');
      }
      const origTransform = element.getAttribute('data-orig-transform') || '';
      
      const isMatch = elId.includes(normalizedPart) || normalizedPart.includes(elId.replace(/^(part|label)-/, ''));

      if (isMatch) {
        element.style.opacity = '1.0';
        element.style.filter = `drop-shadow(0 0 16px ${color}) drop-shadow(0 0 6px ${color})`;
        element.style.transformBox = 'fill-box';
        element.style.transformOrigin = 'center';
        
        const scaleStr = pulseScale !== 1.0 ? ` scale(${pulseScale.toFixed(3)})` : ' scale(1.12)';
        element.setAttribute('transform', origTransform + scaleStr);
        element.style.transition = 'transform 0.15s ease, filter 0.2s ease, opacity 0.3s ease';

        // Highlight label background rects/circles/paths
        const shapes = element.querySelectorAll('rect, circle, path, polygon');
        shapes.forEach(shape => {
          if (!shape.hasAttribute('data-orig-stroke')) {
            shape.setAttribute('data-orig-stroke', shape.getAttribute('stroke') || '');
            shape.setAttribute('data-orig-stroke-width', shape.getAttribute('stroke-width') || '');
          }
          shape.setAttribute('stroke', color);
          shape.setAttribute('stroke-width', '4.5');
        });

        // Highlight text elements
        const texts = element.querySelectorAll('text');
        texts.forEach(text => {
          if (!text.hasAttribute('data-orig-fill')) {
            text.setAttribute('data-orig-fill', text.getAttribute('fill') || '');
          }
          text.setAttribute('fill', '#ffffff');
          text.setAttribute('font-weight', '900');
          text.style.filter = `drop-shadow(0 0 4px ${color})`;
        });

      } else {
        // Dim non-matching parts slightly to draw focus
        element.style.opacity = '0.35';
        element.style.filter = 'none';
        element.setAttribute('transform', origTransform);
      }
    });
  }

  private resetAllParts(): void {
    if (!this.container) return;
    const allParts = this.container.querySelectorAll('[id^="part-"], [id^="label-"], [class*="part-"], [class*="label-"]');
    allParts.forEach(el => {
      const element = el as SVGElement;
      element.style.opacity = '1.0';
      element.style.filter = 'none';
      if (element.hasAttribute('data-orig-transform')) {
        element.setAttribute('transform', element.getAttribute('data-orig-transform') || '');
      }
      
      const shapes = element.querySelectorAll('rect, circle, path, polygon');
      shapes.forEach(shape => {
        if (shape.hasAttribute('data-orig-stroke')) {
          const origStroke = shape.getAttribute('data-orig-stroke');
          if (origStroke) shape.setAttribute('stroke', origStroke);
          else shape.removeAttribute('stroke');
          const origW = shape.getAttribute('data-orig-stroke-width');
          if (origW) shape.setAttribute('stroke-width', origW);
          else shape.removeAttribute('stroke-width');
        }
      });

      const texts = element.querySelectorAll('text');
      texts.forEach(text => {
        if (text.hasAttribute('data-orig-fill')) {
          const origFill = text.getAttribute('data-orig-fill');
          if (origFill) text.setAttribute('fill', origFill);
          text.style.filter = 'none';
        }
      });
    });
  }
}
